import React, {
  ReactNode,
  FunctionComponent,
  useContext,
  useEffect,
} from "react";
import i18n from "i18next";
import {
  Box,
  Button,
  Container,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useKey } from "react-use";
import { MandatorContext } from "@rentablo/theming";
import AugsburgerLogo from "@images/banks-full/ausburger.svg?react";
import ArrowLeft from "@icons/myicons/arrow-left.svg?react";
import FnzLogo from "@images/banks-full/fnz.svg?react";
import FondsdepotLogo from "@images/banks-full/fondsdepot.svg?react";
import FFBLogo from "@images/banks-full/ffb.svg?react";
import DabLogo from "@images/banks-full/dab-bnp.svg?react";
import ComdirectLogo from "@images/banks-full/comdirect.svg?react";
import MetzlerLogo from "@images/banks-full/metzler.svg?react";
import DWSLogo from "@images/banks-full/dws.svg?react";
import MorgenFundLogo from "@images/banks-full/morgenfund.svg?react";
import { getWorkflowType } from "@features/cashbackWizard2.0/utils/getWorkflowType";
import { getStepGroup } from "@features/cashbackWizard2.0/utils/getStepGroup";
import { ChangeDataContext, Header, RateSupport } from "@components";
import { LoadingButton } from "@rentablo/components";
import { titleKeyFactory } from "@features/cashbackWizard2.0/utils/titleKeyFactory";
import { CashbackWizardContext } from "@features/cashbackWizard2.0/types";
import {
  useGoogleTagManagerHandlers,
  useGoogleTagManagerVirtualPage,
} from "@hooks";

export const WizardStepContainer = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.default};
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
`;

const StyledContainer = styled(Container)`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(0, 6, 14)} !important;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: ${({ theme }) => theme.spacing(0, 4, 4)} !important;
  }
`;

const getTitle = (
  titleKey: string,
  isSubtitle = false,
  isOnvista = false,
  ctx?: CashbackWizardContext
) => {
  if (!titleKey) return undefined;

  const mainTitleKey = `cashbackWizard.${titleKey}.${
    isSubtitle ? "subtitle" : "title"
  }`;
  let title =
    i18n.exists(mainTitleKey) &&
    typeof i18n.t(mainTitleKey, { returnObjects: true }) === "string"
      ? i18n.t(mainTitleKey)
      : undefined;

  if (ctx && !title) {
    const headings = titleKeyFactory(ctx, isOnvista);
    const existingHeadings = headings.reduce((acc, heading) => {
      const titleKeyWithSubState = `${mainTitleKey}.${[...acc, heading].join(
        "_"
      )}`;
      if (i18n.exists(titleKeyWithSubState)) {
        return [...acc, heading];
      }
      return acc;
    }, [] as string[]);

    if (existingHeadings.length > 0) {
      title = i18n.t(`${mainTitleKey}.${existingHeadings.join("_")}`);
    } else if (i18n.exists(`${mainTitleKey}.default`)) {
      title = i18n.t(`${mainTitleKey}.default`);
    }
  }

  return title;
};

interface StepContainerProps {
  onNext?: () => void;
  onPrevious?: () => void;
  titleKey?: string;
  context?: CashbackWizardContext;
  dependency?: unknown;
  children: ReactNode;
  testId?: string;
  isOptional?: boolean;
  NotificationComponent?: ReactNode;
  HelpComponent?: FunctionComponent<{ onClose: () => void }>;
  helpText?: string;
  isNextDisabled?: boolean;
  nextButtonText?: string;
  nextButtonHref?: string;
  isFetching?: boolean;
  bankLogoBic?: Bic;
  maxWidth?: number;
  isRateSupportShown?: boolean;
  headerMarginBottom?: number;
}

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(11)};
  gap: ${({ theme }) => theme.spacing(4)};
`;

const banks: Record<Bic, React.ReactNode> = {
  DABBDEMMXXX: <DabLogo />,
  COBADEHDXXX: <ComdirectLogo width="93px" height="16px" />,
  COBADEMXXXX: <FnzLogo />,
  FODBDE77XXX: <FondsdepotLogo />,
  FFBKDEFFTHK: <FFBLogo />,
  METZDEFFXXX: <MetzlerLogo />,
  AUGBDE77XXX: <AugsburgerLogo />,
  DEUTLU2DXXX: <MorgenFundLogo width="100px" height="30px" />,
  DEUTDEFFXXX: <DWSLogo width="70px" />,
};

const BankLogo = ({ bic }: { bic: Bic }) => {
  const theme = useTheme();
  const logo = banks[bic];
  return logo ? (
    <Box
      mb={6.5}
      sx={{ "& svg, & img": { maxWidth: "100px", height: "auto" } }}
      color={theme.palette.mode === "dark" ? "white" : "black"}
    >
      {logo}
    </Box>
  ) : null;
};

const BackButton = ({
  onPrevious,
  hasNextButton,
}: {
  onPrevious: () => void;
  hasNextButton: boolean;
}) => {
  const { t } = useTranslation();
  const { isOnvista } = useContext(MandatorContext);
  const isMoreSm = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  return isMoreSm || !hasNextButton ? (
    <Button
      variant={isOnvista ? "secondary" : "tertiary"}
      onClick={onPrevious}
      fullWidth={!isMoreSm && !hasNextButton}
      data-testid="actions-previous"
      sx={{
        flexShrink: 0,
      }}
      startIcon={isOnvista ? undefined : <ArrowLeft height={24} width={24} />}
    >
      {t("navigation.previous")}
    </Button>
  ) : (
    <Button
      variant={isOnvista ? "secondary" : "tertiary"}
      onClick={onPrevious}
      data-testid="actions-previous"
      sx={{ flexShrink: 0 }}
    >
      <ArrowLeft height={24} width={24} />
    </Button>
  );
};

export const StepContainer = ({
  testId,
  titleKey,
  context,
  onNext,
  onPrevious,
  dependency,
  children,
  isOptional = false,
  isNextDisabled = false,
  isFetching = false,
  NotificationComponent,
  helpText,
  HelpComponent,
  nextButtonText,
  nextButtonHref,
  bankLogoBic,
  maxWidth = 736,
  isRateSupportShown = true,
  headerMarginBottom = 11,
}: StepContainerProps) => {
  const { t } = useTranslation();
  const { isOnvista } = useContext(MandatorContext);
  const dispatch = useContext(ChangeDataContext);
  const title = getTitle(titleKey || "", false, isOnvista, context);
  const subtitle = getTitle(titleKey || "", true, isOnvista, context);

  useKey(
    "Enter",
    (event) => {
      if (
        event.target instanceof Element &&
        // don't react on clicking action-previous and next
        !event.target.closest("[data-testid='actions-previous']") &&
        !event.target.closest("[data-testid='next']")
      ) {
        onNext && onNext();
      }
    },
    {},
    [JSON.stringify(dependency)]
  );

  const { onClick } = useGoogleTagManagerHandlers({
    userFlowId: "cashback-wizard",
    ignoredSelectors: [
      "#account-holder-type",
      "[data-testid='experience-certificates-and-options-with-leverage']",
      "[data-testid='experience-certificates-and-options-without-leverage']",
      "[data-testid='experience-domestic-bonds']",
      "[data-testid='experience-domestic-stocks']",
      "[data-testid='experience-foreign-currency']",
      "[data-testid='experience-foreign-stocks']",
      "[data-testid='experience-funds']",
      "[data-testid='experience-foreign-bonds']",
      "[data-testid='experience-other']",
      "[data-testid='select-nationality-another']",
      "[data-testid='taxation-one-more']",
      "[data-testid='us-tax-liability']",
      "[data-testid='pep']",
      "[data-testid='access-to-insider-info']",
      "[data-testid='start-now-button-top']",
      "[data-testid='has-second-legal-guardian-select']",
    ],
    trackedElements: ["button", "radio-group"],
    data: {
      contextId: testId,
      contextGroup: getStepGroup(testId),
      workflow: getWorkflowType(context),
    },
  });

  useGoogleTagManagerVirtualPage({
    contextId: testId,
    userFlowId: "cashback-wizard",
    pageTitle: title ?? null,
    data: {
      contextGroup: getStepGroup(testId),
      workflow: getWorkflowType(context),
    },
  });

  const showSkip = isOptional && !dependency;
  const nextButtonTextDesc =
    nextButtonText ||
    (showSkip
      ? t("signupWizard.nextButton.skip")
      : t("signupWizard.nextButton.next"));
  const isNextButtonShown = onNext || nextButtonHref;

  useEffect(() => {
    if (!dispatch || !testId) return;

    // this is to let Wizard know which step it is currently in
    dispatch({
      type: "step",
      payload: {
        step: testId,
      },
    });
  }, [testId, dispatch]);

  return (
    <StyledContainer>
      <WizardStepContainer
        onClick={onClick}
        sx={{ maxWidth }}
        data-testid={testId}
        className="wizard-step-container"
      >
        {bankLogoBic && <BankLogo bic={bankLogoBic} />}
        {(title || subtitle) && (
          <Header
            title={title}
            subtitle={subtitle}
            mb={headerMarginBottom}
            variant={isOnvista ? "h1" : "h4"}
          />
        )}
        {children}
        <ActionsContainer>
          {onPrevious && (
            <BackButton
              onPrevious={onPrevious}
              hasNextButton={Boolean(onNext)}
            />
          )}
          {isNextButtonShown && (
            <LoadingButton
              id="wizard-submit"
              data-testid="next"
              onClick={() => onNext && onNext()}
              fullWidth
              disableElevation
              variant={showSkip ? "tertiary" : "primary"}
              disabled={isNextDisabled}
              isFetching={isFetching}
              href={nextButtonHref}
            >
              {nextButtonTextDesc}
            </LoadingButton>
          )}
        </ActionsContainer>
        {isRateSupportShown && <RateSupport />}
      </WizardStepContainer>
    </StyledContainer>
  );
};
